import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import {
  Building2,
  CalendarDays,
  Download,
  ExternalLink,
  FileText,
  FolderOpen,
  Hash,
  Loader2,
  Trash2,
  User,
} from "lucide-react";
import {
  getAllContractDocuments,
  getContractDocumentStats,
  deleteContractDocument,
} from "../../../api/crm/contract-documents/info";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../components/ConfirmDialog";
import { confirmDelete, DELETE_ENTITY } from "../../../utils/confirmDelete";
import { hasPermission } from "../../../utils/permissionUtils";
import Pagination from "../../../components/common/Pagination";
import ContractDocumentMiniDashboard from "./ContractDocumentMiniDashboard";
import ContractLinkedDossierFilter from "./ContractLinkedDossierFilter";
import ContractLinkedDossierUploadModal from "./ContractLinkedDossierUploadModal";
import {
  DOC_TYPE_LABELS,
  extractFileName,
  formatFileSize,
} from "./contractDocumentUtils";

const ContractLinkedDossierPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const contractIdFromUrl = searchParams.get("contract_id") || "";
  const openUploadFromUrl = searchParams.get("upload") === "1";

  const [documents, setDocuments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [filters, setFilters] = useState({
    keyword: "",
    branch_id: "",
    contract_id: contractIdFromUrl,
    doc_type: "",
    quick: null,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [total, setTotal] = useState(0);
  const [showUpload, setShowUpload] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const canCreate = hasPermission("crm.documents", "C");
  const canDelete = hasPermission("crm.documents", "D");

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (contractIdFromUrl) {
      setFilters((prev) => ({ ...prev, contract_id: contractIdFromUrl, quick: null }));
    }
  }, [contractIdFromUrl]);

  useEffect(() => {
    if (openUploadFromUrl && canCreate) {
      setShowUpload(true);
      const next = new URLSearchParams(searchParams);
      next.delete("upload");
      setSearchParams(next, { replace: true });
    }
  }, [openUploadFromUrl, canCreate, searchParams, setSearchParams]);

  const resolvedDocType = useMemo(() => {
    if (filters.quick === "unsigned" || filters.quick === "signed") return filters.quick;
    return filters.doc_type || "";
  }, [filters.quick, filters.doc_type]);

  const resolvedContractId = useMemo(() => {
    if (filters.quick === "all_contracts") return "";
    return filters.contract_id || "";
  }, [filters.quick, filters.contract_id]);

  const fetchStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const params = {};
      if (filters.branch_id) params.branch_id = filters.branch_id;
      if (resolvedContractId) params.contract_id = resolvedContractId;
      const data = await getContractDocumentStats(params);
      setStats(data);
    } catch (err) {
      console.error(err);
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  }, [filters.branch_id, resolvedContractId]);

  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: pageSize,
      };
      if (filters.keyword?.trim()) params.q = filters.keyword.trim();
      if (filters.branch_id) params.branch_id = filters.branch_id;
      if (resolvedContractId) params.contract_id = resolvedContractId;
      if (resolvedDocType) params.doc_type = resolvedDocType;

      const data = await getAllContractDocuments(params);
      setDocuments(Array.isArray(data?.items) ? data.items : []);
      setTotal(data?.total || 0);
    } catch (err) {
      console.error(err);
      showErrorToast("Không thể tải hồ sơ liên kết");
      setDocuments([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, filters.keyword, filters.branch_id, resolvedContractId, resolvedDocType]);

  useEffect(() => {
    fetchDocuments();
    fetchStats();
  }, [fetchDocuments, fetchStats]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters, pageSize]);

  const refreshAll = () => {
    fetchDocuments();
    fetchStats();
  };

  const handleDashboardCardClick = (key) => {
    setFilters((prev) => {
      if (key === "total") {
        return { ...prev, quick: null, doc_type: "" };
      }
      if (key === "all_contracts") {
        const next = prev.quick === "all_contracts" ? null : "all_contracts";
        return { ...prev, quick: next, contract_id: next ? "" : prev.contract_id };
      }
      if (key === "unsigned" || key === "signed") {
        const next = prev.quick === key ? null : key;
        return {
          ...prev,
          quick: next,
          doc_type: next || (prev.doc_type === key ? "" : prev.doc_type),
        };
      }
      return prev;
    });
  };

  const handleDelete = async (doc) => {
    const confirm = await confirmDelete({
      type: DELETE_ENTITY.document,
      name: doc.file_name || extractFileName(doc.file_url),
    });
    if (!confirm.isConfirmed) return;

    try {
      await deleteContractDocument(doc.id);
      showSuccessToast("Đã xóa hồ sơ liên kết");
      refreshAll();
    } catch (err) {
      console.error(err);
      showErrorToast("Không thể xóa hồ sơ");
    }
  };

  const emptyMessage = useMemo(() => {
    if (
      filters.keyword ||
      filters.branch_id ||
      filters.contract_id ||
      filters.doc_type ||
      filters.quick
    ) {
      return "Không tìm thấy hồ sơ phù hợp";
    }
    return "Chưa có hồ sơ liên kết — tải chứng từ từ Quản lý hợp đồng hoặc nút Tải lên";
  }, [filters]);

  const renderRowActions = (doc) => (
    <div className="flex items-center justify-center gap-1">
      <a
        href={doc.file_url}
        target="_blank"
        rel="noopener noreferrer"
        className="rounded-lg p-2 text-gray-400 transition hover:bg-red-50 hover:text-red-600"
        title="Tải về"
      >
        <Download size={17} />
      </a>
      {canDelete && (
        <button
          type="button"
          onClick={() => handleDelete(doc)}
          className="rounded-lg p-2 text-gray-400 transition hover:bg-red-50 hover:text-red-600"
          title="Xóa"
        >
          <Trash2 size={17} />
        </button>
      )}
    </div>
  );

  const renderDocCard = (doc) => (
    <div
      key={doc.id}
      className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="inline-flex rounded-full bg-red-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-red-700">
            {DOC_TYPE_LABELS[doc.doc_type] || doc.doc_type}
          </div>
          <p className="mt-2 break-words text-sm font-bold text-gray-900">
            {doc.title || doc.file_name || extractFileName(doc.file_url)}
          </p>
          {doc.title && (
            <p className="mt-0.5 break-all text-[11px] text-gray-400">
              {doc.file_name || extractFileName(doc.file_url)}
            </p>
          )}
          <p className="mt-1 text-xs font-semibold text-gray-500">
            {doc.contract_code} — {doc.contract_title}
          </p>
          <p className="mt-1 text-xs text-gray-400">{doc.customer_name}</p>
        </div>
        <div className="text-right text-[10px] font-medium text-gray-400">
          {doc.created_at ? format(new Date(doc.created_at), "dd/MM/yyyy") : "—"}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-gray-50 pt-3 text-[11px] text-gray-400">
        <span>{formatFileSize(doc.file_size)}</span>
        {doc.doc_code && (
          <span className="inline-flex items-center gap-1 font-semibold text-gray-500">
            <Hash size={11} />
            {doc.doc_code}
          </span>
        )}
        {doc.doc_date && (
          <span className="inline-flex items-center gap-1">
            <CalendarDays size={11} />
            {format(new Date(doc.doc_date), "dd/MM/yyyy")}
          </span>
        )}
        <div className="ml-auto flex gap-1">{renderRowActions(doc)}</div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6 p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div data-tour="documents-header">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="rounded-2xl border border-red-700/10 bg-red-700/5 p-3 text-red-700 shadow-sm sm:rounded-[1.5rem] sm:p-4">
              <FolderOpen className="h-5 w-5 sm:h-6 sm:w-6" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl font-black uppercase tracking-tight leading-tight text-gray-800 sm:text-2xl">
                Hồ sơ <span className="text-red-700">liên kết</span>
              </h1>
              <p className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.16em] text-gray-400 sm:mt-1 sm:tracking-[0.2em]">
                Lưu trữ hồ sơ, chứng từ liên quan đến hợp đồng
              </p>
            </div>
          </div>
        </div>
      </div>

      <ContractDocumentMiniDashboard
        stats={stats}
        loading={statsLoading}
        activeKey={filters.quick}
        onCardClick={handleDashboardCardClick}
      />

      <ContractLinkedDossierFilter
        filters={filters}
        setFilters={setFilters}
        onRefresh={refreshAll}
        onUploadClick={() => setShowUpload(true)}
      />

      {isMobile ? (
        <div className="space-y-3">
          {loading ? (
            <div className="flex flex-col items-center gap-3 py-12">
              <Loader2 className="h-9 w-9 animate-spin text-red-600" />
              <p className="text-sm font-semibold text-gray-500">Đang tải hồ sơ...</p>
            </div>
          ) : documents.length === 0 ? (
            <div className="flex flex-col items-center gap-3 rounded-2xl border border-gray-100 bg-white py-14">
              <FileText className="h-10 w-10 text-gray-300" />
              <p className="px-6 text-center text-sm font-semibold text-gray-500">{emptyMessage}</p>
            </div>
          ) : (
            documents.map((doc) => renderDocCard(doc))
          )}
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full table-auto border-collapse">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50 text-left">
                  <th className="p-4 text-xs font-bold uppercase tracking-wider text-gray-400">Hồ sơ</th>
                  <th className="p-4 text-xs font-bold uppercase tracking-wider text-gray-400">Loại</th>
                  <th className="p-4 min-w-[200px] text-xs font-bold uppercase tracking-wider text-gray-400">Hợp đồng</th>
                  <th className="p-4 min-w-[180px] text-xs font-bold uppercase tracking-wider text-gray-400">Khách hàng</th>
                  <th className="p-4 text-xs font-bold uppercase tracking-wider text-gray-400">Ngày tải</th>
                  <th className="p-4 w-[100px] text-center text-xs font-bold uppercase tracking-wider text-gray-400">Thao tác</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {loading ? (
                  <tr>
                    <td colSpan="6" className="p-12 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <Loader2 className="h-9 w-9 animate-spin text-red-600" />
                        <p className="text-sm font-semibold text-gray-500">Đang tải hồ sơ liên kết...</p>
                      </div>
                    </td>
                  </tr>
                ) : documents.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="p-14 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <FileText className="h-10 w-10 text-gray-300" />
                        <p className="text-sm font-semibold text-gray-500">{emptyMessage}</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  documents.map((doc) => (
                    <tr key={doc.id} className="group hover:bg-red-50/30">
                      <td className="p-4 align-top">
                        <div className="flex items-start gap-2">
                          <FolderOpen size={16} className="mt-0.5 shrink-0 text-red-500" />
                          <div className="min-w-0">
                            <p className="break-words text-sm font-bold text-gray-900">
                              {doc.title || doc.file_name || extractFileName(doc.file_url)}
                            </p>
                            {doc.title && (
                              <p className="break-all text-[11px] text-gray-400">
                                {doc.file_name || extractFileName(doc.file_url)}
                              </p>
                            )}
                            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-gray-400">
                              <span>{formatFileSize(doc.file_size)}</span>
                              {doc.doc_code && (
                                <span className="inline-flex items-center gap-1 font-semibold text-gray-500">
                                  <Hash size={11} />
                                  {doc.doc_code}
                                </span>
                              )}
                              {doc.doc_date && (
                                <span className="inline-flex items-center gap-1">
                                  <CalendarDays size={11} />
                                  {format(new Date(doc.doc_date), "dd/MM/yyyy")}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="p-4 align-top">
                        <span className="inline-flex rounded-full border border-gray-100 bg-gray-50 px-2.5 py-1 text-[10px] font-bold uppercase text-gray-600">
                          {DOC_TYPE_LABELS[doc.doc_type] || doc.doc_type}
                        </span>
                      </td>
                      <td className="p-4 align-top">
                        <div className="space-y-1">
                          <span className="inline-flex rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-bold text-red-700">
                            {doc.contract_code}
                          </span>
                          <p className="text-sm font-semibold text-gray-800">{doc.contract_title}</p>
                          <div className="flex items-center gap-1 text-[11px] text-gray-400">
                            <Building2 size={12} />
                            {doc.branch_name || "—"}
                          </div>
                        </div>
                      </td>
                      <td className="p-4 align-top">
                        <div className="flex items-center gap-2">
                          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100">
                            <User size={14} className="text-slate-500" />
                          </div>
                          <span className="text-sm font-semibold text-gray-800">
                            {doc.customer_name || "—"}
                          </span>
                        </div>
                      </td>
                      <td className="p-4 align-top text-sm font-medium text-gray-600">
                        {doc.created_at ? format(new Date(doc.created_at), "dd/MM/yyyy HH:mm") : "—"}
                      </td>
                      <td className="p-4 align-top">{renderRowActions(doc)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && total > 0 && (
        <Pagination
          totalRecords={total}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
        />
      )}

      <p className="flex items-center gap-2 text-xs font-medium text-gray-400">
        <ExternalLink size={14} />
        Thêm hồ sơ khi soạn hợp đồng hoặc dùng nút Tải lên hồ sơ trong bộ lọc.
      </p>

      <ContractLinkedDossierUploadModal
        isOpen={showUpload}
        onClose={() => setShowUpload(false)}
        onSuccess={refreshAll}
        defaultContractId={filters.contract_id}
      />
    </div>
  );
};

export default ContractLinkedDossierPage;
